import { isLoanJumbo } from "./isLoanJumbo";

export const CONFORMING_LOAN_LIMIT = 806500;

const MINIMUM_DOWN_PAYMENT_LOOKUP = {
  Conventional: {
    Single: {
      Primary: {
        Purchase: {
          "First Time": 3,
          Default: 5,
        },
        Refinance: 5,
        Cashout: 20,
      },
      Secondary: {
        Purchase: 10,
        Refinance: 10,
        Cashout: 25,
      },
      Rental: {
        Purchase: 20,
        Refinance: 25,
        Cashout: 25,
      },
    },
    "2+": {
      Primary: {
        Purchase: 5,
        Refinance: 5,
        Cashout: 25,
      },
      Secondary: {
        // N/A
      },
      Rental: {
        Purchase: 25,
        Refinance: 25,
        Cashout: 30,
      },
    },
  },
  "Conventional Hi Balance": {
    Single: {
      Primary: {
        Purchase: 5,
        Refinance: 5,
        Cashout: 20,
      },
      Secondary: {
        Purchase: 10,
        Refinance: 10,
        Cashout: 25,
      },
      Rental: {
        Purchase: 20,
        Refinance: 25,
        Cashout: 25,
      },
    },
    Double: {
      Primary: {
        Purchase: 15,
        Refinance: 15,
        Cashout: 25,
      },
      Secondary: {
        // N/A
      },
      Rental: {
        Purchase: 25,
        Refinance: 25,
        Cashout: 30,
      },
    },
    "3+": {
      Primary: {
        Purchase: 25,
        Refinance: 25,
        Cashout: 25,
      },
      Secondary: {
        // N/A
      },
      Rental: {
        Purchase: 25,
        Refinance: 25,
        Cashout: 30,
      },
    },
  },
  Jumbo: {
    Single: {
      Primary: {
        Purchase: {
          "< $1M": 20,
          "< $2M": 25,
          "< $3M": 30,
        },
        Refinance: {
          "< $1M": 20,
          "< $2M": 25,
          "< $3M": 30,
        },
        Cashout: {
          "< $1M": 30,
          "< $2M": 35,
          // < $3M = N/A
        },
      },
      Secondary: {
        Purchase: {
          "< $1.5M": 30,
        },
        Refinance: {
          "< $1.5M": 30,
        },
        Cashout: {
          // N/A
        },
      },
    },
    Double: {
      Primary: {
        Purchase: {
          "< $1M": 20,
          "< $2M": 25,
          "< $3M": 30,
        },
        Refinance: {
          "< $1M": 20,
          "< $2M": 25,
          "< $3M": 30,
        },
        Cashout: {
          "< $1M": 30,
          "< $2M": 35,
          // < $3M = N/A
        },
      },
      Secondary: {
        // N/A
      },
      Rental: {
        // N/A
      },
    },
    "3+": {
      Primary: {
        Purchase: {
          "< $2M": 30,
          "< $3M": 35,
        },
        Refinance: {
          "< $2M": 30,
          "< $3M": 35,
        },
        Cashout: {
          // N/A
        },
      },
      Secondary: {
        // N/A
      },
      Rental: {
        // N/A
      },
    },
  },
};

export const getDownPaymentMinimum = ({
  countyLimits,
  county,
  state,
  countyFips,
  stateFips,
  loanAmount,
  propertyType,
  occupancyType,
  lookingTo,
  isFirstTimeHomebuyer,
}) => {
  // Calculate loan size
  let loanSize;
  if (loanAmount > CONFORMING_LOAN_LIMIT) {
    const isJumbo = isLoanJumbo({
      countyLimits,
      county,
      state,
      countyFips,
      stateFips,
      loanAmount,
      propertyType,
    });
    loanSize = isJumbo ? "Jumbo" : "Conventional Hi Balance";
  } else {
    loanSize = "Conventional";
  }

  // Calculate number of units relevant to loan size
  let numberOfUnits;
  if (loanSize === "Conventional") {
    // Duplex, Triplex, Quadplex
    numberOfUnits = "2+";

    if (["Townhome", "Condo", "Single"].includes(propertyType)) {
      numberOfUnits = "Single";
    }
  } else {
    // Single, Townhome, Condo
    numberOfUnits = "Single";

    if (propertyType === "Duplex") {
      numberOfUnits = "Double";
    }

    if (["Triplex", "Quadplex"].includes(propertyType)) {
      numberOfUnits = "3+";
    }
  }

  let loanType =
    lookingTo === "Build"
      ? "Purchase"
      : lookingTo === "RefinanceAndCashOut"
      ? "Cashout"
      : lookingTo;

  // firstTime: Only used for purchased primary, single conventional properties
  let firstTime = ["true", true].includes(isFirstTimeHomebuyer)
    ? "First Time"
    : "Default";

  // loanAmountGrade: Only used for jumbo loans
  let loanAmountGrade;
  if (
    occupancyType === "Primary" &&
    (numberOfUnits === "Single" || numberOfUnits === "Double")
  ) {
    if (loanAmount < 1000000) {
      loanAmountGrade = "< $1M";
    } else if (loanAmount < 2000000) {
      loanAmountGrade = "< $2M";
    } else if (loanAmount < 3000000) {
      loanAmountGrade = "< $3M";
    }
  } else if (occupancyType === "Primary" && numberOfUnits === "3+") {
    if (loanAmount < 2000000) {
      loanAmountGrade = "< $2M";
    } else if (loanAmount < 3000000) {
      loanAmountGrade = "< $3M";
    }
  } else if (
    occupancyType === "Secondary" &&
    numberOfUnits === "Single" &&
    loanAmount < 1500000
  ) {
    loanAmountGrade = "< $1.5M";
  }

  // Calculate minimumDownPayment using lookup structure and fields calculated above
  let minimumDownPayment =
    MINIMUM_DOWN_PAYMENT_LOOKUP[loanSize]?.[numberOfUnits]?.[occupancyType]?.[
      loanType
    ];
  if (
    loanSize === "Conventional" &&
    numberOfUnits === "Single" &&
    occupancyType === "Primary" &&
    loanType === "Purchase"
  ) {
    minimumDownPayment = minimumDownPayment?.[firstTime];
  }
  if (loanSize === "Jumbo") {
    minimumDownPayment = minimumDownPayment?.[loanAmountGrade];
  }

  return minimumDownPayment;
};
